import { useState } from "react";
import { Card, CardContent } from "../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Button } from "../ui/button";
import MapView from "../map-view";
import {
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  MapPin,
  Plus,
  Minus,
  RotateCcw,
} from "lucide-react";
import {
  TransformComponent,
  TransformWrapper,
  useControls,
} from "react-zoom-pan-pinch";

export const PropertyGallery = ({ property }) => {
  const {
    photosAvailable,
    photoUrls = [],
    latitude,
    longitude,
    address,
    city,
    state,
  } = property || {};

  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const fullAddress = `${address || ""}, ${city || ""}, ${state || ""}`.trim();

  const handlePrevious = () => {
    setSelectedPhotoIndex((prev) =>
      prev === 0 ? photoUrls.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    setSelectedPhotoIndex((prev) =>
      prev === photoUrls.length - 1 ? 0 : prev + 1
    );
  };

  const defaultTab =
    photosAvailable && photoUrls?.length > 0 ? "photos" : "map";

  const ZoomControls = () => {
    const { zoomIn, zoomOut, resetTransform } = useControls();

    return (
      <div className="absolute bottom-2 sm:bottom-4 right-2 sm:right-4 flex gap-1 z-40">
        <div className="bg-black/90 rounded-lg flex shadow-lg p-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => zoomIn()}
            className="text-white hover:text-white hover:bg-white/20 h-8 w-8 sm:h-10 sm:w-10"
            title="Zoom In"
          >
            <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => zoomOut()}
            className="text-white hover:text-white hover:bg-white/20 h-8 w-8 sm:h-10 sm:w-10"
            title="Zoom Out"
          >
            <Minus className="h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => resetTransform()}
            className="text-white hover:text-white hover:bg-white/20 h-8 w-8 sm:h-10 sm:w-10"
            title="Reset Zoom"
          >
            <RotateCcw className="h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Card className="overflow-hidden shadow-xl">
      <Tabs defaultValue={defaultTab} className="w-full">
        <TabsList className="w-full rounded-none border-b">
          {photosAvailable && photoUrls?.length > 0 && (
            <TabsTrigger value="photos" className="flex-1 text-xs sm:text-sm">
              <ImageIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              <span className="hidden xs:inline">
                Photos ({photoUrls.length})
              </span>
              <span className="xs:hidden">Photos</span>
            </TabsTrigger>
          )}
          <TabsTrigger value="map" className="flex-1 text-xs sm:text-sm">
            <MapPin className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
            <span className="hidden xs:inline">Map View</span>
            <span className="xs:hidden">Map</span>
          </TabsTrigger>
        </TabsList>

        {/* Photos Tab */}
        {photosAvailable && photoUrls?.length > 0 && (
          <TabsContent value="photos" className="m-0">
            <CardContent className="p-0">
              {/* Main Photo */}
              <div className="relative bg-black aspect-square">
                <TransformWrapper
                  defaultScale={1}
                  defaultPositionX={0}
                  defaultPositionY={0}
                  wheel={{ disabled: false }}
                  pinch={{ disabled: false }}
                  panning={{ velocityDisabled: false }}
                  pinchSensitivity={1.5}
                  doubleClick={{ disabled: false }}
                  smooth={true}
                  minScale={0.5}
                  maxScale={4}
                >
                  <TransformComponent
                    wrapperClass="!w-full !h-full"
                    contentClass="!w-full !h-full flex items-center justify-center"
                  >
                    <img
                      src={photoUrls[selectedPhotoIndex]}
                      alt={`Property ${selectedPhotoIndex + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </TransformComponent>
                  <ZoomControls />
                </TransformWrapper>

                {/* Photo Counter */}
                <div className="absolute top-2 sm:top-4 right-2 sm:right-4 bg-black/70 text-white px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium z-50">
                  {selectedPhotoIndex + 1} / {photoUrls.length}
                </div>

                {/* Navigation Buttons */}
                {photoUrls.length > 1 && (
                  <>
                    <Button
                      variant="secondary"
                      size="icon"
                      className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 h-10 w-10 sm:h-12 sm:w-12 rounded-full shadow-lg bg-white/90 hover:bg-white z-50"
                      onClick={handlePrevious}
                    >
                      <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" />
                    </Button>
                    <Button
                      variant="secondary"
                      size="icon"
                      className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 h-10 w-10 sm:h-12 sm:w-12 rounded-full shadow-lg bg-white/90 hover:bg-white z-50"
                      onClick={handleNext}
                    >
                      <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6" />
                    </Button>
                  </>
                )}
              </div>

              {/* Thumbnails */}
              {photoUrls.length > 1 && (
                <div className="p-2 sm:p-4 bg-muted/30">
                  <div className="flex gap-2 sm:gap-3 overflow-x-auto py-2 px-1 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
                    {photoUrls.map((url, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedPhotoIndex(index)}
                        className={`relative flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-lg overflow-hidden border-2 transition-all ${
                          index === selectedPhotoIndex
                            ? "border-primary shadow-lg scale-105"
                            : "border-transparent hover:border-gray-300"
                        }`}
                      >
                        <img
                          src={url}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        {index === selectedPhotoIndex && (
                          <div className="absolute inset-0 bg-primary/20 pointer-events-none" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </TabsContent>
        )}

        {/* Map Tab */}
        <TabsContent value="map" className="m-0">
          <CardContent className="p-0">
            <div className="relative bg-muted">
              <MapView
                latitude={latitude}
                longitude={longitude}
                width={"100%"}
                height={500}
              />
              <div className="absolute bottom-2 sm:bottom-4 left-2 sm:left-4 right-2 sm:right-4 bg-white/95 backdrop-blur-sm p-3 sm:p-4 rounded-lg shadow-lg">
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Map View
                </p>
                <p className="text-sm sm:text-base font-semibold">
                  {fullAddress}
                </p>
              </div>
            </div>
          </CardContent>
        </TabsContent>
      </Tabs>
    </Card>
  );
};

export default PropertyGallery;
